import { chatRoomPath, chatSpacePath } from "@/react/helpers/urlHelpers";
import type { ChatRoom } from "@/react/types/Chat/ChatRoom";
import type { ChatRoomMessage } from "@/react/types/Chat/ChatRoomMessage";

export const getCopyLinkUrl = ({
  id,
  parentMessageId,
  chatRoomUrl,
}: {
  id: number;
  parentMessageId?: number;
  chatRoomUrl?: string;
}) => {
  const hostUrl =
    window.location.protocol +
    "//" +
    window.location.host +
    (chatRoomUrl || window.location.pathname);
  return (
    hostUrl +
    (parentMessageId
      ? `?message_id=${id}#message_${parentMessageId}`
      : `#message_${id}`)
  );
};

export const doesActiveMessageIdMatches = ({
  activeMessageId,
  id,
  isMessageThread = false,
}: {
  activeMessageId: string | null;
  id: number;
  isMessageThread: boolean;
}) => activeMessageId === getActiveMessageId(id, isMessageThread);

export const getActiveMessageId = (id: number, isMessageThread: boolean) =>
  `${isMessageThread ? "thread-" : "message-"}${String(id)}`;

export const getDefaultMessagePortalId = () =>
  document.getElementById("message-thread-scroll-view") ||
  document.getElementById("message-scroll-view") ||
  document.body;

export const getChatRoomUrlWithMessageId = (
  chatRoom: {
    slug: string;
    uuid: string;
  },
  message: ChatRoomMessage,
) => {
  const { slug, uuid } = chatRoom;
  const {
    id,
    parent_message_id: parentMessageId,
    embedded: isEmbedded,
  } = message;

  const chatRoomUrl = isEmbedded
    ? chatSpacePath({ spaceSlug: slug })
    : chatRoomPath({ uuid });

  let messageParams = `#message_${id}`;
  if (parentMessageId) {
    messageParams = `?message_id=${id}#message_${parentMessageId}`;
  }

  return `${chatRoomUrl}${messageParams}`;
};

let tempMessageId = 0;
const nextTempMessageId = () => {
  tempMessageId--;
  return tempMessageId;
};

export const createTempChatRoomMessage = ({
  chatRoom,
  richTextBody,
  localAttachments,
  localSgidToObjectMap,
  parentMessageId,
  creationUuid,
  sentAt,
}: {
  chatRoom: ChatRoom;
  richTextBody: any;
  localAttachments: any;
  localSgidToObjectMap: any;
  parentMessageId?: number;
  creationUuid: string;
  sentAt: string;
}) => {
  const currentParticipant = chatRoom.current_participant;
  const currentParticipantId = currentParticipant?.id;
  if (!currentParticipant || !currentParticipantId) {
    return null;
  }
  const tempMessage: ChatRoomMessage = {
    id: nextTempMessageId(),
    chat_room_id: chatRoom.id,
    rich_text_body: {
      ...richTextBody,
      attachments: localAttachments,
      sgids_to_object_map: localSgidToObjectMap,
    },
    body: "",
    chat_room_uuid: chatRoom.uuid,
    chat_room_participant_id: currentParticipantId,
    creation_uuid: creationUuid,
    sender: currentParticipant,
    reactions: [],
    parent_message_id: parentMessageId ?? null,
    updated_at: sentAt,
    sent_at: sentAt,
    created_at: sentAt,
  };

  if (parentMessageId) {
    tempMessage.parent_message_id = parentMessageId;
  }

  return tempMessage;
};
